import { useEffect } from 'react'

import { useToast } from '@/hooks'
import { useAuthStateChange } from '@/modules/auth'
import { useWebsocketContext } from '@/modules/websocketContext'
import { redirectTo } from '@/utils'
import { useAsyncError } from '../errors'
import {
  type AccountStoreActions,
  type CreateAccountParams,
  getAccount,
  useAccount,
  useAccountActions,
  useAccountLoaders,
  useAccountMutations,
} from './accountStore'
import { useCreditBankLoaders, useCreditBankMutations } from './creditBankStore'
import { useCreditBundlesLoaders } from './creditBundlesStore'
import {
  useCurrentUserActions,
  useCurrentUserLoaders,
} from './currentUserStore'
import { useInvitationLoaders } from './invitationStore'
import { useMembersActions, useMembersLoaders } from './membersStore'
import type { AccountDTO, AccountSummaryDTO, Wallet } from './types'

export const useFetchAccount = () => {
  const account = useAccount()
  const loaders = useAccountLoaders()
  const { reset } = useAccountActions()
  const throwError = useAsyncError()

  useEffect(() => {
    loaders.fetchAccount().catch(throwError)
  }, [])

  useEffect(() => {
    if (account?.id) {
      loaders.fetchAccountSummary().catch(throwError)
    }
  }, [account])

  useAuthStateChange({ onSignedOut: reset })
}

interface EventData {
  action: WebsocketAction
  account: AccountDTO
  summary: AccountSummaryDTO
}

type WebsocketAction = keyof typeof HANDLERS

const HANDLERS = {
  UPDATE_ACCOUNT: (data: EventData, actions: AccountStoreActions) => {
    const { setAccount, setSummary } = actions
    const current = getAccount()
    setAccount({
      ...data.account,
      isOwner: current?.isOwner ?? data.account.is_owner,
      isAssumed: current?.isAssumed ?? data.account.is_assumed,
    })
    setSummary(data.summary)
  },
}

export const useAccountWebsocket = () => {
  const { isReady, subscribe, unsubscribe } = useWebsocketContext()
  const actions = useAccountActions()

  useEffect(() => {
    if (!isReady) return

    const subscription = subscribe(
      { channel: 'AccountChannel' },
      {
        received: (data: unknown) => {
          const eventData = data as EventData
          const handler = HANDLERS[eventData.action]
          if (!handler) return
          handler(eventData, actions)
        },
      },
    )
    return () => unsubscribe(subscription)
  }, [isReady])
}

export const useFetchCurrentUser = () => {
  const loaders = useCurrentUserLoaders()
  const { reset } = useCurrentUserActions()

  useEffect(() => {
    loaders.fetchCurrentUser()
  }, [loaders])

  useAuthStateChange({ onSignedOut: reset })
}

export const useFetchMembers = () => {
  const account = useAccount()
  const { setAccountId } = useMembersActions()
  const { fetchMembers, fetchInvitations } = useMembersLoaders()
  const throwError = useAsyncError()

  useEffect(() => {
    setAccountId(account?.id || null)
    if (!account) return

    fetchMembers().catch(throwError)
    fetchInvitations().catch(throwError)
  }, [account])
}

// Reconciliation is premium, but the cycle-by-cycle view is not. The server withholds the verdict on
// its own, so the SPA reads this flag only to drop the verdict wording and the override buttons.
export const useReconciliationEntitled = () => {
  const account = useAccount()
  return account?.isReconciliationEnabled ?? false
}

export const useFetchInvitation = (token: string) => {
  const { fetchInvitation } = useInvitationLoaders()
  const throwError = useAsyncError()

  useEffect(() => {
    fetchInvitation(token).catch(throwError)
  }, [token])
}

// Billing/checkout application service: composes the account domain operations
// that return a Stripe/portal URL with the browser redirect and uniform failure
// handling, so components express intent ("renew this project") instead of
// re-implementing the produce-URL-then-navigate dance.
export const useFetchCreditBundles = (wallet: Wallet) => {
  const { fetchCreditBundles } = useCreditBundlesLoaders()
  const throwError = useAsyncError()

  useEffect(() => {
    fetchCreditBundles(wallet).catch(throwError)
  }, [wallet])
}

export const useFetchCreditBank = () => {
  const { fetchCreditBank } = useCreditBankLoaders()
  const throwError = useAsyncError()

  useEffect(() => {
    fetchCreditBank('pages').catch(throwError)
    fetchCreditBank('screenings').catch(throwError)
  }, [])
}

export const useCheckout = () => {
  const { errorToast } = useToast()
  const { purchaseProject, selectPlan, createAccount } = useAccountMutations()
  const { purchaseCredits } = useCreditBankMutations()
  const { fetchCustomerPortalUrl } = useAccountLoaders()

  const redirectToBillingUrl = async (
    produceUrl: () => Promise<string>,
    failureMessage: string,
  ) => {
    try {
      redirectTo(await produceUrl())
    } catch (error) {
      errorToast(failureMessage, error as Error)
    }
  }

  return {
    renewProject: (slug: string) =>
      redirectToBillingUrl(
        () => purchaseProject({ slug }),
        'Failed to renew project',
      ),
    buyProject: () =>
      redirectToBillingUrl(() => purchaseProject(), 'Failed to start checkout'),
    buyCredits: (priceId: string) =>
      redirectToBillingUrl(
        () => purchaseCredits(priceId),
        'Failed to purchase credits',
      ),
    selectPlan: (priceId: string) =>
      redirectToBillingUrl(
        () => selectPlan(priceId),
        'Failed to create checkout for your plan',
      ),
    createAccountCheckout: (params: CreateAccountParams) =>
      redirectToBillingUrl(
        async () => (await createAccount(params)).checkoutUrl,
        'Failed to create account',
      ),
    openCustomerPortal: () =>
      redirectToBillingUrl(
        fetchCustomerPortalUrl,
        'Failed to open billing portal',
      ),
  }
}
